import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, MessageCircle, Check } from 'lucide-react';
import ArborescenceLogo from '../components/ArborescenceLogo';
import '../styles/PublicQuoteView.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicQuoteView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [services, setServices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [paymentModalities, setPaymentModalities] = useState([]);
  
  // User selections
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedModalityId, setSelectedModalityId] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  
  // UI state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQuoteData();
  }, [token]);

  const fetchQuoteData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/quotes/${token}`);
      
      setQuote(response.data.quote);
      setClient(response.data.client);
      setServices(response.data.services);
      setDepartments(response.data.departments);
      setPaymentModalities(response.data.payment_modalities);
      
      // Pre-select all services by default
      const allServiceIds = response.data.quote.services.map(s => s.service_id);
      setSelectedServices(allServiceIds);
      
      setLoading(false);
    } catch (error) {
      setError('Devis introuvable ou lien expiré');
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    if (selectedServices.includes(serviceId)) {
      setSelectedServices(selectedServices.filter(id => id !== serviceId));
    } else {
      setSelectedServices([...selectedServices, serviceId]);
    }
  };

  const calculateTotal = () => {
    if (!quote) return { subtotal: 0, tps: 0, tvq: 0, total: 0 };
    
    // Calculate subtotal based on selected services
    const subtotal = quote.services
      .filter(s => selectedServices.includes(s.service_id))
      .reduce((sum, s) => sum + (s.sous_total || s.total || 0), 0);
    
    // Apply "premier intervenant" discount if applicable
    const afterDiscount = quote.premier_intervenant 
      ? subtotal * 0.8  // 20% discount
      : subtotal;
    
    const tps = afterDiscount * 0.05;      // 5%
    const tvq = afterDiscount * 0.09975;   // 9.975%
    const total = afterDiscount + tps + tvq;
    
    return {
      subtotal: afterDiscount,
      tps,
      tvq,
      total
    };
  };

  const getAvailableModalities = () => {
    // Filter modalities based on selected services
    // For now, return all active modalities
    // TODO: Filter based on service constraints
    return paymentModalities;
  };

  const handleAccept = async () => {
    if (selectedServices.length === 0) {
      alert('Veuillez sélectionner au moins un service');
      return;
    }
    
    if (!selectedModalityId) {
      alert('Veuillez choisir une modalité de paiement');
      return;
    }
    
    if (!window.confirm('Confirmer l\'acceptation de ce devis?')) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/public/quotes/${token}/accept`, {
        selected_services: selectedServices,
        payment_modality_id: selectedModalityId
      });
      
      // Success - show confirmation
      alert('✅ Devis accepté avec succès! Nous vous contacterons sous peu.');
      navigate('/');
    } catch (error) {
      alert('Erreur lors de l\'acceptation du devis');
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/public/quotes/${token}/reject`, {
        reason: rejectReason
      });
      
      alert('Devis refusé. Merci de nous avoir consultés.');
      navigate('/');
    } catch (error) {
      alert('Erreur lors du refus du devis');
      setSubmitting(false);
    }
  };

  const handleContact = async () => {
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/public/quotes/${token}/contact`, {
        message: contactMessage
      });
      
      alert('✅ Message envoyé! Nous vous contacterons bientôt.');
      navigate('/');
    } catch (error) {
      alert('Erreur lors de l\'envoi du message');
      setSubmitting(false);
    }
  };

  const getDepartmentColor = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (!service) return '#01500A';
    
    const dept = departments.find(d => d.id === service.department_id);
    return dept?.couleur || '#01500A';
  };

  if (loading) {
    return (
      <div className="public-quote-loading">
        <ArborescenceLogo size="large" />
        <p>Chargement du devis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-quote-error">
        <ArborescenceLogo size="large" />
        <h2>❌ {error}</h2>
        <button className="btn-primary" onClick={() => navigate('/')}>
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const totals = calculateTotal();
  const selectedModality = paymentModalities.find(m => m.id === selectedModalityId);

  return (
    <div className="public-quote-view">
      <div className="quote-header">
        <ArborescenceLogo size="medium" />
        <h1>Votre devis</h1>
        <div className="quote-number">{quote.numero_devis}</div>
      </div>

      <div className="quote-container">
        {/* Client Info */}
        <div className="section client-info">
          <h2>Informations client</h2>
          <p><strong>Nom:</strong> {client.prenom} {client.nom}</p>
          <p><strong>Adresse:</strong> {client.adresse}</p>
          <p><strong>Courriel:</strong> {client.courriel}</p>
          {client.propertyArea > 0 && (
            <p><strong>Superficie:</strong> {client.propertyArea.toLocaleString()} pi²</p>
          )}
        </div>

        {/* Services Selection */}
        <div className="section services-section">
          <h2>Services proposés</h2>
          <p className="section-description">
            Sélectionnez les services que vous souhaitez (vous pouvez décocher ceux dont vous n'avez pas besoin)
          </p>
          
          <div className="services-list">
            {quote.services.map((quoteService, index) => {
              const service = services.find(s => s.id === quoteService.service_id);
              const isSelected = selectedServices.includes(quoteService.service_id);
              
              return (
                <div 
                  key={index}
                  className={`service-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleService(quoteService.service_id)}
                >
                  <div className="service-checkbox">
                    {isSelected ? <Check size={20} /> : <div className="checkbox-empty" />}
                  </div>
                  <div 
                    className="service-color-dot"
                    style={{ background: getDepartmentColor(quoteService.service_id) }}
                  />
                  <div className="service-details">
                    <h3>{quoteService.service_nom}</h3>
                    {service?.description && (
                      <p className="service-description">{service.description}</p>
                    )}
                    <div className="service-quantity">
                      {quoteService.quantite.toLocaleString()} {quoteService.unite}
                    </div>
                  </div>
                  <div className="service-price">
                    {(quoteService.sous_total || quoteService.total || 0).toLocaleString('fr-CA', {
                      style: 'currency',
                      currency: 'CAD'
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals */}
        <div className="section totals-section">
          <div className="total-line">
            <span>Sous-total:</span>
            <span>{totals.subtotal.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
          </div>
          {quote.premier_intervenant && (
            <div className="total-line discount">
              <span>Rabais premier intervenant (20%):</span>
              <span>Appliqué ✓</span>
            </div>
          )}
          <div className="total-line">
            <span>TPS (5%):</span>
            <span>{totals.tps.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
          </div>
          <div className="total-line">
            <span>TVQ (9.975%):</span>
            <span>{totals.tvq.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
          </div>
          <div className="total-line final">
            <span>TOTAL:</span>
            <span>{totals.total.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
          </div>
        </div>

        {/* Payment Modality Selection */}
        {selectedServices.length > 0 && (
          <div className="section payment-section">
            <h2>Modalité de paiement</h2>
            <p className="section-description">Choisissez votre mode de paiement préféré</p>
            
            <div className="modality-options">
              {getAvailableModalities().map(modality => (
                <div
                  key={modality.id}
                  className={`modality-option ${selectedModalityId === modality.id ? 'selected' : ''}`}
                  onClick={() => setSelectedModalityId(modality.id)}
                >
                  <div className="modality-radio">
                    {selectedModalityId === modality.id && <div className="radio-dot" />}
                  </div>
                  <div className="modality-content">
                    <h3>{modality.nom}</h3>
                    <p>{modality.description}</p>
                    
                    {modality.type_modalite === 'split_fixed_date' && (
                      <div className="modality-details">
                        <p>• Premier versement: {modality.pourcentage_premier_versement}% ({(totals.total * modality.pourcentage_premier_versement / 100).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })})</p>
                        <p>• Deuxième versement: {100 - modality.pourcentage_premier_versement}% le {new Date(modality.date_deuxieme_versement).toLocaleDateString('fr-CA')}</p>
                      </div>
                    )}
                    
                    {modality.type_modalite === 'bank_transfer' && modality.instructions_virement && (
                      <pre className="bank-instructions">{modality.instructions_virement}</pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="section actions-section">
          <button 
            className="btn-action accept"
            onClick={handleAccept}
            disabled={submitting || selectedServices.length === 0 || !selectedModalityId}
          >
            <CheckCircle size={20} />
            Accepter le devis
          </button>
          
          <button 
            className="btn-action contact"
            onClick={() => setShowContactModal(true)}
            disabled={submitting}
          >
            <MessageCircle size={20} />
            Me contacter
          </button>
          
          <button 
            className="btn-action reject"
            onClick={() => setShowRejectModal(true)}
            disabled={submitting}
          >
            <XCircle size={20} />
            Refuser le devis
          </button>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Refuser le devis</h2>
            <p>Pouvez-vous nous indiquer la raison de votre refus? (optionnel)</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Votre raison..."
              rows="4"
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowRejectModal(false)}>
                Annuler
              </button>
              <button className="btn-danger" onClick={handleReject}>
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Demander à être contacté</h2>
            <p>Laissez-nous un message et nous vous contacterons rapidement</p>
            <textarea
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
              placeholder="Votre message..."
              rows="4"
              required
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowContactModal(false)}>
                Annuler
              </button>
              <button 
                className="btn-primary" 
                onClick={handleContact}
                disabled={!contactMessage.trim()}
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicQuoteView;
